import { createSlice } from "@reduxjs/toolkit";
import { apiCallerExt } from "../../api/apiCallerExt";
import { loyaltyApi } from "../../sharedfiles/EndpointConfig";
import { toaster } from "../../sharedfiles/helper";

/**
 * State for the Loyalty Management hub (/loyalty-management), backed by
 * loyalty_application_BE.
 *
 * Auth is split on the backend: /api/* takes HTTP Basic (shared credential
 * from REACT_APP_LOYALTY_BASIC_AUTH, base64 of user:password), while the
 * brand-settings surface (metadata, history, GraphQL) takes x-api-key
 * (REACT_APP_LOYALTY_API_KEY). CORS on the service allows only GET/POST, so
 * every write here is a POST.
 *
 * v1 reads/writes brand config through the REST config endpoints
 * (getloyaltyconfigsbyId / loyaltyconfigs upsert) rather than the GraphQL
 * mutations — the GraphQL schema's exact arg shapes should be pinned against
 * graphql/schema.js before switching the editors over to per-field mutations.
 */

const basicHeaders = () => ({
  Authorization: `Basic ${process.env.REACT_APP_LOYALTY_BASIC_AUTH || ""}`,
});

const apiKeyHeaders = () => ({
  "x-api-key": process.env.REACT_APP_LOYALTY_API_KEY || "",
});

export const LoyaltySlice = createSlice({
  name: "loyaltyMgmt",
  initialState: {
    // brand settings
    config: null,
    configLoading: false,
    metadata: null,
    // history
    history: [],
    historyTotal: 0,
    historyLoading: false,
    // player lookup
    playerInfo: null,
    playerLoading: false,
    playerTransactions: [],
    leaderboard: [],
    // redemptions
    redemptions: [],
    redemptionsTotal: 0,
    redemptionsLoading: false,
    // missions
    missions: [],
    missionsLoading: false,
    // shared
    submitting: false,
  },
  reducers: {
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    setConfigLoading: (state, action) => {
      state.configLoading = action.payload;
    },
    setMetadata: (state, action) => {
      state.metadata = action.payload;
    },
    setHistory: (state, action) => {
      state.history = action.payload.rows;
      state.historyTotal = action.payload.total;
    },
    setHistoryLoading: (state, action) => {
      state.historyLoading = action.payload;
    },
    setPlayerInfo: (state, action) => {
      state.playerInfo = action.payload;
    },
    setPlayerLoading: (state, action) => {
      state.playerLoading = action.payload;
    },
    setPlayerTransactions: (state, action) => {
      state.playerTransactions = action.payload;
    },
    setLeaderboard: (state, action) => {
      state.leaderboard = action.payload;
    },
    setRedemptions: (state, action) => {
      state.redemptions = action.payload.rows;
      state.redemptionsTotal = action.payload.total;
    },
    setRedemptionsLoading: (state, action) => {
      state.redemptionsLoading = action.payload;
    },
    setMissions: (state, action) => {
      state.missions = action.payload;
    },
    setMissionsLoading: (state, action) => {
      state.missionsLoading = action.payload;
    },
    setSubmitting: (state, action) => {
      state.submitting = action.payload;
    },
  },
});

export const {
  setConfig,
  setConfigLoading,
  setMetadata,
  setHistory,
  setHistoryLoading,
  setPlayerInfo,
  setPlayerLoading,
  setPlayerTransactions,
  setLeaderboard,
  setRedemptions,
  setRedemptionsLoading,
  setMissions,
  setMissionsLoading,
  setSubmitting,
} = LoyaltySlice.actions;

const errText = (error, fallback) =>
  (typeof error?.response === "string" && error.response) || fallback;

// ─── Brand settings ──────────────────────────────────────────────────────────

export const fetchLoyaltyConfig = (operatorId, brand) => (dispatch) => {
  dispatch(setConfigLoading(true));
  return apiCallerExt(loyaltyApi.configById({ operatorId, brand }), "GET", null, basicHeaders())
    .then((res) => {
      // Endpoint may answer with the doc directly, a {data} wrapper, or an array.
      const doc = Array.isArray(res) ? res[0] : res?.data ?? res;
      dispatch(setConfig(doc || null));
      if (!doc) toaster.error("No loyalty config found for this brand/operator");
    })
    .catch((error) => {
      dispatch(setConfig(null));
      toaster.error(errText(error, "Failed to load loyalty config"));
    })
    .finally(() => dispatch(setConfigLoading(false)));
};

export const fetchSettingsMetadata = () => (dispatch) =>
  apiCallerExt(loyaltyApi.settingsMetadata, "GET", null, apiKeyHeaders())
    .then((res) => dispatch(setMetadata(res?.data ?? res)))
    .catch(() => dispatch(setMetadata(null)));

// Upsert through POST /api/loyaltyconfigs. NOTE: when this call creates a NEW
// brand the backend intentionally process.exit(0)s to force a restart — the
// request's connection drops even though the write succeeded.
export const saveLoyaltyConfig = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(loyaltyApi.configs, "POST", payload, basicHeaders())
    .then(() => {
      toaster.success("Loyalty config saved");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Config save failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const exportLoyaltyConfigs = () => () =>
  apiCallerExt(loyaltyApi.configs, "GET", null, basicHeaders())
    .then((res) => {
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "loyalty-configs.json";
      a.click();
      URL.revokeObjectURL(url);
    })
    .catch((error) => toaster.error(errText(error, "Export failed")));

// ─── Settings history ────────────────────────────────────────────────────────

export const fetchSettingsHistory = (filters) => (dispatch) => {
  dispatch(setHistoryLoading(true));
  return apiCallerExt(loyaltyApi.settingsHistory(filters), "GET", null, apiKeyHeaders())
    .then((res) => {
      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.history)
          ? res.history
          : Array.isArray(res)
            ? res
            : [];
      dispatch(setHistory({ rows, total: res?.total ?? rows.length }));
    })
    .catch((error) => {
      dispatch(setHistory({ rows: [], total: 0 }));
      toaster.error(errText(error, "Failed to load history"));
    })
    .finally(() => dispatch(setHistoryLoading(false)));
};

// ─── Player lookup ───────────────────────────────────────────────────────────

export const fetchPlayerLoyalty = (params) => (dispatch) => {
  dispatch(setPlayerLoading(true));
  dispatch(setPlayerInfo(null));
  dispatch(setPlayerTransactions([]));
  return apiCallerExt(loyaltyApi.getUserLoyalty, "POST", params, basicHeaders())
    .then((res) => dispatch(setPlayerInfo(res?.data ?? res)))
    .catch((error) => toaster.error(errText(error, "Player lookup failed")))
    .finally(() => dispatch(setPlayerLoading(false)));
};

export const fetchPlayerHistory = (params) => (dispatch) =>
  apiCallerExt(loyaltyApi.loyaltyHistory, "POST", params, basicHeaders())
    .then((res) => {
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      dispatch(setPlayerTransactions(rows));
    })
    .catch((error) => toaster.error(errText(error, "Failed to load transactions")));

export const fetchLoyaltyLeaderboard = (filters) => (dispatch) =>
  apiCallerExt(loyaltyApi.getLeaderboard(filters), "GET", null, basicHeaders())
    .then((res) => {
      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.leaderboard)
          ? res.leaderboard
          : Array.isArray(res)
            ? res
            : [];
      dispatch(setLeaderboard(rows));
    })
    .catch((error) => toaster.error(errText(error, "Failed to load leaderboard")));

// ─── Redemptions (admin CRUD) ────────────────────────────────────────────────

export const fetchRedemptions = (filters) => (dispatch) => {
  dispatch(setRedemptionsLoading(true));
  return apiCallerExt(loyaltyApi.adminRedemptions(filters), "GET", null, basicHeaders())
    .then((res) => {
      const rows = Array.isArray(res?.data) ? res.data : [];
      dispatch(setRedemptions({ rows, total: res?.totalRecords ?? rows.length }));
    })
    .catch((error) => {
      dispatch(setRedemptions({ rows: [], total: 0 }));
      toaster.error(errText(error, "Failed to load redemptions"));
    })
    .finally(() => dispatch(setRedemptionsLoading(false)));
};

export const saveRedemption = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(loyaltyApi.adminRedemptionUpsert, "POST", payload, basicHeaders())
    .then(() => {
      toaster.success("Redemption saved");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Redemption save failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const deleteRedemption = (id) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(loyaltyApi.adminRedemptionDelete, "POST", { id }, basicHeaders())
    .then(() => {
      toaster.success("Redemption deleted");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Redemption delete failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const fetchMissions = () => (dispatch) => {
  dispatch(setMissionsLoading(true));
  return apiCallerExt(loyaltyApi.adminMissions, "GET", null, basicHeaders())
    .then((res) => dispatch(setMissions(Array.isArray(res?.data) ? res.data : [])))
    .catch((error) => {
      dispatch(setMissions([]));
      toaster.error(errText(error, "Failed to load missions"));
    })
    .finally(() => dispatch(setMissionsLoading(false)));
};

// ─── Missions & ops ──────────────────────────────────────────────────────────

export const createLoyaltyMission = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(loyaltyApi.createMission, "POST", payload, basicHeaders())
    .then(() => {
      toaster.success("Mission created");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Mission creation failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export const triggerLoyaltyReport = (payload) => (dispatch) => {
  dispatch(setSubmitting(true));
  return apiCallerExt(loyaltyApi.reports, "POST", payload, basicHeaders())
    .then(() => {
      toaster.success("Report emailed");
      return { success: true };
    })
    .catch((error) => {
      toaster.error(errText(error, "Report trigger failed"));
      return { success: false };
    })
    .finally(() => dispatch(setSubmitting(false)));
};

export default LoyaltySlice.reducer;
